// montecarlo.ts — tax-aware Monte Carlo / Renditeband: runs many stochastic geometric
// Brownian motion (GBM) return paths through the REAL forecast driver (the exact same
// contribution/growth/TER/AGE/KESt/withdrawal logic runForecast uses — see forecast.ts's
// runForecastMonthlyWithFactors), and aggregates the resulting net-wealth paths into a
// p10/p25/p50/p75/p90 outcome band plus summary statistics. This is the headline
// differentiator versus a generic calculator: the tax path (Meldefonds AGE step-ups,
// KESt, Entnahmeplan depletion) runs identically on every single simulated path, not on
// one deterministic average return.
//
// CORRECTNESS ANCHOR (verified by a dedicated test, montecarlo.test.ts): with
// volatilityBp = 0, monthlyGrowthFactor() below returns the IDENTICAL expression
// runForecast's deterministic driver uses for every month of every path — not a
// mathematically-equivalent exp()/log() formula, but the literal same
// `Math.pow(1 + annualReturnBp/10000, 1/12)` — so every path is byte-identical to
// runForecast's own output, to the cent, and the percentile band has exactly zero width
// at every point. This holds by construction, not floating-point coincidence.
//
// PRNG: mulberry32, a small deterministic 32-bit generator. Math.random() and Date.now()
// are BANNED here (and everywhere a "seed -> reproducible output" contract is required):
// same seed always produces the same infinite draw sequence, so a Monte Carlo run is
// fully reproducible given (config, volatilityBp, paths, seed). Box-Muller turns pairs of
// its uniform draws into N(0,1) samples for the GBM monthly log-return.
import { roundCentsHalfAwayFromZero } from './money.js';
import { addMonths, runForecastMonthlyWithFactors } from './forecast.js';
import { DEFAULT_KEST_BP } from './types.js';
// ---- seeded PRNG ------------------------------------------------------------------------
/**
 * mulberry32: a fast, tiny, deterministic 32-bit PRNG (public-domain algorithm). Same
 * seed -> same output sequence, byte for byte, forever — pure 32-bit integer arithmetic,
 * no Math.random/Date.now or other non-deterministic entropy source. Returns floats in
 * [0, 1).
 */
export function mulberry32(seed) {
    let a = seed >>> 0;
    return function next() {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Box-Muller transform: two U(0,1) draws -> one N(0,1) sample. `u1` is clamped away from
 * exactly 0 (a representable but measure-zero PRNG output) to avoid `Math.log(0) =
 * -Infinity` propagating into a NaN growth factor.
 */
function nextGaussian(rng) {
    const u1 = Math.max(rng(), Number.EPSILON);
    const u2 = rng();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
// ---- percentile helper --------------------------------------------------------------------
/**
 * Linear-interpolated percentile (the common "R-7" / Excel PERCENTILE.INC convention)
 * over a PRE-SORTED ascending array. `p` in [0, 1]; out-of-range `p` is clamped rather
 * than throwing. Throws on an empty array (there is no sane percentile of nothing).
 */
export function percentile(sortedAsc, p) {
    if (sortedAsc.length === 0) {
        throw new RangeError('percentile: empty array');
    }
    const clampedP = Math.min(1, Math.max(0, p));
    const idx = clampedP * (sortedAsc.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    const loVal = sortedAsc[lo];
    if (lo === hi)
        return loVal;
    const hiVal = sortedAsc[hi];
    const frac = idx - lo;
    return loVal + (hiVal - loVal) * frac;
}
function percentileCents(sortedAsc, p) {
    return roundCentsHalfAwayFromZero(percentile(sortedAsc, p));
}
// ---- GBM monthly growth factor ------------------------------------------------------------
/**
 * Monthly GBM growth factor: monthly log-return =
 *   (annualDrift - 0.5*sigma^2) * (1/12) + sigma * sqrt(1/12) * z,   z ~ N(0,1)
 * where annualDrift = ln(1 + annualReturnBp/10000) and sigma = volatilityBp/10000
 * (annual). growthFactor = exp(monthly log-return).
 *
 * sigma === 0 is special-cased to return the LITERAL expression runForecast's
 * deterministic driver uses, `Math.pow(1 + annualReturnBp/10000, 1/12)`, instead of
 * `Math.exp(ln(1+r)/12)` — the two are mathematically equal but not guaranteed
 * bit-identical in float64, and the σ=0 invariant ("every MC path reproduces the
 * deterministic forecast to the cent") must hold by construction, not by floating-point
 * coincidence.
 */
function monthlyGrowthFactor(annualReturnBp, sigma, z) {
    if (sigma === 0) {
        return Math.pow(1 + annualReturnBp / 10000, 1 / 12);
    }
    const annualDrift = Math.log(1 + annualReturnBp / 10000);
    const monthlyLogReturn = (annualDrift - 0.5 * sigma * sigma) / 12 + sigma * Math.sqrt(1 / 12) * z;
    return Math.exp(monthlyLogReturn);
}
export const MC_DEFAULT_PATHS = 500;
export const MC_MAX_PATHS = 2000;
/** Arbitrary fixed default seed (a well-known 32-bit constant, golden-ratio-derived —
 * chosen only for "looks intentional", carries no other significance). */
export const MC_DEFAULT_SEED = 0x9e3779b9;
export const MC_MAX_BAND_POINTS = 120;
// ---- downsampling ---------------------------------------------------------------------------
/**
 * Evenly-spaced indices into [0, total), always including index 0 and total-1, capped at
 * `maxPoints`. Identity (every index, in order) when total <= maxPoints.
 */
function downsampleIndices(total, maxPoints) {
    if (total <= maxPoints) {
        return Array.from({ length: total }, (_, i) => i);
    }
    const indices = new Set();
    const step = (total - 1) / (maxPoints - 1);
    for (let i = 0; i < maxPoints; i++) {
        indices.add(Math.round(i * step));
    }
    return Array.from(indices).sort((a, b) => a - b);
}
/** Lower-median (lower of the two middle elements for even lengths) of a pre-sorted
 * ascending array of month indices, mapped back to its date via `dates`. Deterministic,
 * documented convention — there's no canonical "the" median date for an even-sized set. */
function medianDepletionDate(monthIndicesAsc, dates) {
    if (monthIndicesAsc.length === 0)
        return null;
    const mid = Math.floor((monthIndicesAsc.length - 1) / 2);
    return dates[monthIndicesAsc[mid]] ?? null;
}
// ---- driver ---------------------------------------------------------------------------------
/**
 * Runs `mc.paths` stochastic GBM paths (monthly log-returns, see the module header)
 * through the exact same forecast driver `runForecast` uses (AGE/KESt/TER/withdrawal all
 * included, every month, per path), and aggregates net wealth (value + settlement) into a
 * percentile band over time plus final-outcome statistics. Always reuses
 * `runForecastMonthlyWithFactors` — the tax/event logic is never forked or reimplemented.
 *
 * Only monthly-granularity configs are supported (GBM is defined as monthly steps here;
 * `granularity: 'annual'` exists solely for GOLDEN_CASES.md case C verification per
 * ENGINE_SPEC.md and has no monthly step structure to inject into).
 */
export function runMonteCarlo(config, mc) {
    if ((config.granularity ?? 'monthly') !== 'monthly') {
        throw new RangeError('runMonteCarlo: only monthly-granularity forecasts are supported');
    }
    const paths = Math.min(MC_MAX_PATHS, Math.max(1, Math.floor(mc.paths)));
    const sigma = mc.volatilityBp / 10000;
    const kestBp = config.kestBp ?? DEFAULT_KEST_BP;
    const totalMonths = config.months + (config.withdrawal?.months ?? 0);
    const hasWithdrawal = config.withdrawal !== undefined;
    // Full nominal date sequence, computed directly from config (via forecast.ts's own
    // addMonths — same expression runForecastMonthlyWithFactors uses for every series
    // point), NOT read off any single path's result.series. A withdrawal phase that
    // depletes stops early (runWithdrawalPhase breaks the loop the month value hits 0), so
    // an individual path's series can be SHORTER than totalMonths — reading dates from
    // "path 0" would silently break the moment path 0 happened to be one that depletes
    // early. Dates never depend on the random growth path anyway, so there is no downside
    // to computing them once, up front, independent of every path.
    const seriesDates = Array.from({ length: totalMonths }, (_, i) => addMonths(config.startDate, i));
    const bandIndices = downsampleIndices(totalMonths, MC_MAX_BAND_POINTS);
    const bandValuesByIndex = bandIndices.map(() => []);
    const finalNetWealths = [];
    let contributedTotalCents = 0;
    let depletedCount = 0;
    const depletionMonthIndices = [];
    const rng = mulberry32(mc.seed >>> 0);
    for (let p = 0; p < paths; p++) {
        // Sequential closure: runForecastMonthlyWithFactors calls this exactly once per
        // elapsed month, strictly in increasing global-month order (see its own doc comment),
        // so a plain "draw the next RNG value" closure — ignoring the index it's handed — is
        // sufficient and correct. σ=0: skip drawing entirely (every path becomes identical,
        // and the deterministic monthlyGrowthFactor branch never touches z anyway).
        const growthFactorAt = () => {
            const z = sigma === 0 ? 0 : nextGaussian(rng);
            return monthlyGrowthFactor(config.annualReturnBp, sigma, z);
        };
        const result = runForecastMonthlyWithFactors(config, kestBp, growthFactorAt);
        const lastPoint = result.series[result.series.length - 1];
        for (let b = 0; b < bandIndices.length; b++) {
            const idx = bandIndices[b];
            // A path whose withdrawal phase depleted early has a series SHORTER than
            // totalMonths — carry the depleted (or otherwise final) net wealth forward for any
            // band point beyond that path's last recorded month. This is the honest "absorbing
            // state" convention: once the Depot is at 0 it stays at 0, it doesn't just vanish
            // from the statistics.
            const pt = idx < result.series.length ? result.series[idx] : lastPoint;
            bandValuesByIndex[b].push(pt.valueCents + pt.settlementCents);
        }
        finalNetWealths.push(lastPoint.valueCents + lastPoint.settlementCents);
        if (p === 0) {
            // Contributions don't depend on returns — identical across every path.
            contributedTotalCents = result.waterfall.contributedCents;
        }
        if (hasWithdrawal) {
            const depletionDate = result.summary.withdrawal?.depletionDate ?? null;
            if (depletionDate !== null) {
                depletedCount++;
                const monthIdx = result.series.findIndex((pt) => pt.date === depletionDate);
                if (monthIdx !== -1)
                    depletionMonthIndices.push(monthIdx);
            }
        }
    }
    const band = bandIndices.map((seriesIdx, bandIdx) => {
        const values = bandValuesByIndex[bandIdx].slice().sort((a, b) => a - b);
        return {
            date: seriesDates[seriesIdx],
            p10: percentileCents(values, 0.1),
            p25: percentileCents(values, 0.25),
            p50: percentileCents(values, 0.5),
            p75: percentileCents(values, 0.75),
            p90: percentileCents(values, 0.9),
        };
    });
    const sortedFinal = finalNetWealths.slice().sort((a, b) => a - b);
    const meanCents = roundCentsHalfAwayFromZero(finalNetWealths.reduce((sum, v) => sum + v, 0) / finalNetWealths.length);
    const lossCount = finalNetWealths.filter((v) => v < contributedTotalCents).length;
    depletionMonthIndices.sort((a, b) => a - b);
    const finalStats = {
        p10: percentileCents(sortedFinal, 0.1),
        p50: percentileCents(sortedFinal, 0.5),
        p90: percentileCents(sortedFinal, 0.9),
        mean: meanCents,
        probLossVsContributed: lossCount / paths,
        ...(hasWithdrawal
            ? {
                probDepleted: depletedCount / paths,
                medianDepletionDate: medianDepletionDate(depletionMonthIndices, seriesDates),
            }
            : {}),
    };
    return {
        band,
        finalStats,
        echo: { paths, volatilityBp: mc.volatilityBp, seed: mc.seed },
    };
}
//# sourceMappingURL=montecarlo.js.map